<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import MiiaFwConfig from '@/configs/config'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { signout } from '@/features/miiaFw/utils/miiaFwCognito'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useStoreUtils } from '@/composables/common/store/useStoreUtils'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { BNavbar, BNavbarNav, BNavItem, BNavText, BNavForm, BButton } from 'bootstrap-vue-next'
import type { LinkMenu } from '@/types/component/header/pageHeader'
import { useRouterUtils } from '@/router/useRouterUtils'
import { HeaderMenu } from '@/constants/ui/header/pageHeader'

const miiaFwStore = useMiiaFwStore()
const disasterStore = useDisasterStore()
const { userName, isAuthorized, attributes } = storeToRefs(miiaFwStore)
const { disasterName } = storeToRefs(disasterStore)
const commonModalStore = useCommonModalStore()
const storeUtils = useStoreUtils()
const { getAuthorizedPaths } = useRouterUtils()

const authorizedDisasterName = computed(() => (isAuthorized.value ? disasterName.value : ''))

const linkMenuList = computed<LinkMenu[]>(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authority = (attributes.value as any)?.attribute?.['disaster-dup']?.authority
  if (authority == null) {
    return []
  }

  const authorizedPaths = getAuthorizedPaths(authority)
  return HeaderMenu.filter(menu => authorizedPaths.some(authPath => authPath.path === menu.path))
})

/**
 * @callback openSignout
 * @description サインアウト用のモーダル画面を開く
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignoutConfirm = async (): Promise<void> => {
  await commonModalStore
    .openConfirmModal({ body: 'サインアウトします。\nよろしいですか？' })
    // OK押下時
    .then(async () => {
      await signout()
      if (router.currentRoute.value.path !== ROUTER_PATHS.login)
        router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
      storeUtils.allReset()
    })
    // キャンセル押下時は何もしない
    .catch(() => {})
}
</script>

<template>
  <BNavbar class="navbar">
    <div class="header-margin" v-if="!isAuthorized">
      <div class="header-logo">
        <img
          src="@/assets/img/black_Slogan_with_red.png"
          width="152"
          alt="FUJIFILM Value from Innovation"
        />
      </div>
      <span class="sys-name">{{ MiiaFwConfig.config.sysName }}</span>
    </div>

    <BNavbarNav v-if="isAuthorized">
      <BNavItem
        v-for="linkMenu in linkMenuList"
        class="link-text"
        :class="{ 'current-link-text': router.currentRoute.value.path == linkMenu.path }"
        :to="linkMenu.path"
        :key="linkMenu.path"
      >
        <div class="highlight-bar" v-if="router.currentRoute.value.path == linkMenu.path"></div>
        {{ linkMenu.displayLabel }}
      </BNavItem>
    </BNavbarNav>
    <BNavbarNav v-if="isAuthorized">
      <BNavText class="disaster-name" right>{{ authorizedDisasterName }}</BNavText>
      <BNavText class="user-name" right>{{ userName }}</BNavText>
      <BNavForm>
        <BButton
          class="btn my-2 my-sm-0 mr-sm-2 btn-info signoutButton"
          @click="openSignoutConfirm"
        >
          <font-awesome-icon icon="sign-out-alt" />&nbsp;{{ $t('Auth.signOut') }}
        </BButton>
      </BNavForm>
    </BNavbarNav>
  </BNavbar>
</template>

<style lang="scss" scoped>
nav {
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  box-shadow: 0 4px 12px 0 rgba(19, 37, 85, 0.16);
  margin-top: 0px !important;
}
.navbar {
  padding: 1.2rem 1.5rem;
}
.header-margin {
  display: flex;
  width: 74%;
  align-items: center;
}

.header-right {
  display: flex;
  width: 25%;
  justify-content: right;
  align-items: center;
}

:deep(.header-margin) .commonDropdown {
  width: 100%;
}

.header-logo {
  margin: 12.5px;
  margin-bottom: 9.92px;
}

.sys-name {
  margin-right: 1%;
  font-size: 1.6rem;
  white-space: nowrap;
}

.disaster-name,
.user-name {
  margin-right: 8px;
  font-size: 1.6rem;
  white-space: nowrap;
}
.link-text {
  color: #8a8a8a;
  font-size: 16px;
  text-decoration: none;
}
.current-link-text {
  font-weight: 700;
}
.highlight-bar {
  background-color: #47ddb5;
  height: 10px;
  width: 100%;
  position: absolute;
  top: -12px;
  left: 0;
}
.circle-info {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid;
  font-size: 2rem;
  border-radius: 50%;
  margin: 10px;
}
.signoutButton {
  margin-left: 12px;
  font-size: 12px;
  color: #fff;
}
</style>
